import * as React from 'react';
import { useState } from 'react';
import { Layout, BackTop } from 'antd';
import { FileDoneOutlined, ToolOutlined, DollarOutlined, ProfileOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import moment from 'moment';
import useMountEffect from '../../hooks/useMountEffect';
import ScrollToTopOnMount from '../scroll_to_top_on_mount/ScrollToTopOnMount';
import {
	getInternalTeamId,
	getInternalTeamName,
	getInternalTeamRecords,
	internalTeamsThunkForRole,
	INTERNAL_TEAMS_PAGINATION,
	INTERNAL_TEAMS_TC,
	INTERNAL_TEAM_FILTERS,
} from '../internal_team_select/internalTeams';
import { PageLoadingPlaceholder } from '../page_loading_placeholder/PageLoadingPlaceholder';
import {
	getCurrency,
	getEntityById,
	nullSafeGet,
	nullSafeGetOrElse,
} from '../../utils/DataAccessUtils';
import OWResult from '../OWResult';
import { OWStatCard } from '../ow_stat_card/OWStatCard';
import BudgetVsDeliverablesChart from '../charts/BudgetVsDeliverablesChart';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import {
	projectsRestCrudThunksForBuyer,
	projectsRestCrudThunksForSupplier,
} from '../../thunks/projects_thunks';
import {
	deliverablesRestCrudThunksForBuyer,
	deliverablesRestCrudThunksForSupplier,
} from '../../thunks/deliverables_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';

const OVERVIEW_DELIVERABLES_TC = 'PROJECT_OVERVIEW_DELIVERABLES';

const { Content } = Layout;

// A muted-below-value meta row (Figma right sidebar). Renders nothing when the
// value is empty so unsupported fields (teams, budget type, entities) simply
// don't appear.
const MetaRow = ({ value, label }: any) =>
	value ? (
		<div className="border-0 border-b border-solid border-grey-4 py-3 last:border-b-0">
			<div className="bodyregular whitespace-pre-line text-character-primary">{value}</div>
			<div className="footnoteregular text-character-secondary">{label}</div>
		</div>
	) : null;

// A stat card with a left summary (icon + label + big total) and a divided
// breakdown column — shared shape for the Deliverables and Work Orders cards.
const BreakdownCard = ({ icon, label, total, rows }: any) => (
	<OWStatCard className="flex items-center gap-3">
		<div className="flex flex-col gap-2">
			<div className="flex items-center gap-1">
				{icon}
				<span className="bodyregular text-character-secondary">{label}</span>
			</div>
			<span className="h4medium text-character-primary">{total}</span>
		</div>
		{rows ? (
			<div className="flex flex-1 flex-col border-0 border-l border-solid border-grey-5 pl-3">
				{rows.map((row) => (
					<div key={row.label} className="flex items-start gap-1">
						<span className="footnoteregular flex-1 text-character-secondary">{row.label}</span>
						<span className="footnoteMedium text-character-primary">{row.value}</span>
					</div>
				))}
			</div>
		) : null}
	</OWStatCard>
);

const ProjectsDetailOverviewV2Page = ({
	currentUser,
	project,
	deliverables,
	internalTeams,
	getProject,
	fetchDeliverables,
	fetchInternalTeams,
	match,
}) => {
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	useMountEffect(() => {
		const projectId = match.params.id;
		const userType = nullSafeGetOrElse('userType', currentUser, 'buyer');
		getProject(userType, projectId)
			.then((record) => {
				setLoading(false);
				// The project carries only supplierFacilityIds, so fetch the internal teams
				// to name them — and only for a project that is actually scoped to some.
				if (nullSafeGetOrElse('supplierFacilityIds', record, []).length) {
					fetchInternalTeams(userType);
				}
			})
			.catch((err) => {
				setLoading(false);
				setError(err);
			});
		fetchDeliverables(projectId);
	});

	if (error) {
		return (
			<OWResult
				error={error}
				title="You no longer have access to this project."
				subTitle="Either its deleted or been reassigned."
			/>
		);
	}
	if (loading) {
		return <PageLoadingPlaceholder />;
	}

	const currency = getCurrency({ currentUser });
	const fmt = (amount) =>
		currency ? currency.symbolFormat(amount, 0) : `$${(Number(amount) || 0).toLocaleString()}`;

	// Deliverables rollup (Not Started / In Progress / Completed).
	const deliverableMetrics = nullSafeGet('deliverableProjectMetrics', project);
	const deliverableRows = deliverableMetrics
		? [
				{
					label: 'Not Started',
					value: nullSafeGetOrElse('deliverablesNotStarted', deliverableMetrics, 0),
				},
				{
					label: 'In Progress',
					value: nullSafeGetOrElse('deliverablesInProgress', deliverableMetrics, 0),
				},
				{
					label: 'Completed',
					value: nullSafeGetOrElse('deliverablesCompleted', deliverableMetrics, 0),
				},
		  ]
		: null;
	const deliverablesTotal = deliverableRows
		? deliverableRows.reduce((sum, row) => sum + row.value, 0)
		: 0;

	// Work-order rollup. No "Paid" bucket at project level yet; In Progress folds
	// in on-hold, matching the existing summary.
	const woMetrics = nullSafeGet('workOrderProjectMetrics', project);
	const woRows = woMetrics
		? [
				{
					label: 'Not Started',
					value: nullSafeGetOrElse('workOrdersNotStarted', woMetrics, 0),
				},
				{
					label: 'In Progress',
					value:
						nullSafeGetOrElse('workOrdersInProgress', woMetrics, 0) +
						nullSafeGetOrElse('workOrdersOnHold', woMetrics, 0),
				},
				{ label: 'Completed', value: nullSafeGetOrElse('workOrdersClosed', woMetrics, 0) },
		  ]
		: null;
	const woTotal = woRows ? woRows.reduce((sum, row) => sum + row.value, 0) : 0;

	const taskMetrics = nullSafeGet('taskProjectMetrics', project);
	const budget = nullSafeGet('budget', project);
	const hasBudget = budget != null;
	// "Used" = approved invoice spend (same source as the old Budget widget).
	const budgetUsed = Number(
		nullSafeGetOrElse('invoiceProjectMetrics.totalInvoicesAmountApproved', project, 0)
	);
	const budgetPct =
		hasBudget && Number(budget) > 0 ? Math.round((budgetUsed * 100) / Number(budget)) : 0;

	const startDate = nullSafeGet('startDate', project);
	const endDate = nullSafeGetOrElse('endDate', project, nullSafeGet('dueDate', project));
	const dateRange =
		startDate || endDate
			? `${startDate ? moment(startDate).format('MMM D, YYYY') : '—'} - ${
					endDate ? moment(endDate).format('MMM D, YYYY') : '—'
			  }`
			: '';
	const templateName = nullSafeGet('projectTemplate.name', project);
	const description = nullSafeGet('description', project);
	// Which internal teams can see this project. An empty list means every internal
	// team can, which is how projects behaved before the field existed. The project
	// carries ids only, so names come from the internal teams fetched above — until
	// they arrive, show the count rather than a row full of ids.
	const scopedTeamIds = nullSafeGetOrElse('supplierFacilityIds', project, []);
	const scopedTeamNames = internalTeams
		.filter((team) => scopedTeamIds.includes(getInternalTeamId(team)))
		.map(getInternalTeamName);
	const internalTeamsValue = !scopedTeamIds.length
		? 'All internal teams'
		: scopedTeamNames.length
		? scopedTeamNames.join('\n')
		: `${scopedTeamIds.length} internal team${scopedTeamIds.length === 1 ? '' : 's'}`;

	return (
		<Content className="ProjectsDetailOverviewPage bg-white" style={{ padding: '16px' }}>
			<ScrollToTopOnMount />
			<BackTop />

			<div className="mb-4 flex flex-wrap gap-3">
				<BreakdownCard
					icon={<FileDoneOutlined className="text-[12px] text-character-secondary" />}
					label="Deliverables"
					total={deliverablesTotal}
					rows={deliverableRows}
				/>
				<BreakdownCard
					icon={<ToolOutlined className="text-[12px] text-character-secondary" />}
					label="Work Orders"
					total={woTotal}
					rows={woRows}
				/>
				<OWStatCard className="flex flex-col justify-center gap-2">
					<div className="flex items-center gap-1">
						<DollarOutlined className="text-[12px] text-character-secondary" />
						<span className="bodyregular text-character-secondary">Budget</span>
						{hasBudget ? (
							<>
								<span className="bodyregular text-character-secondary">·</span>
								<span className="footnoteMedium text-character-primary">{budgetPct}% Used</span>
							</>
						) : null}
					</div>
					<span className="h4medium text-character-primary">
						{hasBudget ? `${fmt(budgetUsed)} / ${fmt(budget)}` : '—'}
					</span>
				</OWStatCard>
				<OWStatCard className="flex flex-col justify-center gap-2">
					<div className="flex items-center gap-1">
						<ProfileOutlined className="text-[12px] text-character-secondary" />
						<span className="bodyregular text-character-secondary">Tasks</span>
					</div>
					<span className="h4medium text-character-primary">
						{taskMetrics
							? `${nullSafeGetOrElse('tasksCompleted', taskMetrics, 0)}/${nullSafeGetOrElse(
									'tasksTotal',
									taskMetrics,
									0
							  )}`
							: '—'}
					</span>
				</OWStatCard>
			</div>

			<div className="flex flex-wrap gap-4">
				<div className="min-w-0 flex-1 rounded-[2px] border border-solid border-grey-5 p-4">
					<div className="h5medium mb-2 text-character-primary">
						Budget vs Deliverables Completed
					</div>
					<BudgetVsDeliverablesChart
						deliverables={deliverables}
						projectBudget={budget}
						currency={currency}
					/>
				</div>
				<div className="w-[320px] shrink-0 rounded-[2px] border border-solid border-grey-5 px-4 py-1">
					<MetaRow value={description} label="Scope" />
					<MetaRow value={dateRange} label="Start Date - End Date" />
					<MetaRow value={templateName} label="Project Template" />
					<MetaRow value={internalTeamsValue} label="Internal Teams" />
				</div>
			</div>
		</Content>
	);
};

const mapStateToProps = (state, ownProps) => ({
	project: getEntityById(nullSafeGet('match.params.id', ownProps), state.projects),
	fetching: state.projects.fetching,
	currentUser: state.session.currentUser,
	deliverables: getRecordsForTargetCollection(state.deliverables, OVERVIEW_DELIVERABLES_TC),
	internalTeams: getInternalTeamRecords(state.supplier_private_networks),
	match: ownProps.match,
	history: ownProps.history,
});

const mapDispatchToProps = (dispatch) => ({
	getProject: (userType, id) =>
		dispatch(
			userType === ROLE_TYPES.SUPPLIER
				? projectsRestCrudThunksForSupplier.readOne(id)
				: projectsRestCrudThunksForBuyer.readOne(id)
		),
	fetchInternalTeams: (userType) =>
		dispatch(
			internalTeamsThunkForRole(userType)(
				{},
				INTERNAL_TEAMS_TC,
				INTERNAL_TEAMS_PAGINATION,
				null,
				INTERNAL_TEAM_FILTERS
			)
		),
	fetchDeliverables: (projectId) =>
		dispatch((innerDispatch, getState) => {
			const userType = nullSafeGetOrElse('userType', getState().session.currentUser, 'buyer');
			const thunks =
				userType === ROLE_TYPES.SUPPLIER
					? deliverablesRestCrudThunksForSupplier
					: deliverablesRestCrudThunksForBuyer;
			return innerDispatch(
				thunks.read({ projectId }, OVERVIEW_DELIVERABLES_TC, { current: 1, pageSize: 200 })
			);
		}),
});

export default withRouter(
	connect(mapStateToProps, mapDispatchToProps)(ProjectsDetailOverviewV2Page)
);
